import { Box, ButtonGroup, Icon, ToggleButton } from '@zillow/constellation-10';
import { useMainLayout } from './MainLayoutContext';
import { IconLocationFilled, IconTrendingFilled } from '@zillow/constellation-icons';
import { MainLayoutType } from './MainLayoutContext';
import { TabsEnum } from './MainPageLayout.config';
import { usePagesWithMap } from './MainPageLayout';
import { PropertiesSelect } from './PropertiesSelect';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import React from 'react';
import {
  emitAdSpendSwitchToMapViewEvent,
  emitAdSpendSwitchToReportViewEvent,
  emitMarketDataSwitchToMapViewEvent,
  emitMarketDataSwitchToReportViewEvent,
  emitOverviewSwitchToMapViewEvent,
  emitOverviewSwitchToReportViewEvent,
} from '../../../analytics/events';
import { useAuth } from '../../../AuthProvider';

export const PropertiesAndLayoutTypeTopBar = ({ activeTab }: { activeTab: TabsEnum }) => {
  const { allProperties, layout, setLayout, selectedFilters } = useMainLayout();
  const { user } = useAuth();
  const pagesWithMap = usePagesWithMap();

  const hasMfhRoles = React.useMemo(() => {
    return user?.isMultifamilyCustomer;
  }, [user]);

  if (allProperties.length === 0) return null;

  const selectedProperty = allProperties.find(
    (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
  );
  const isSingleFamilyPropertySelected = selectedProperty?.aggregateStats.isSingleFamily ?? false;

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: { base: 'column', md: 'row' },
        alignItems: { md: 'center' },
        justifyContent: 'space-between',
        paddingInline: '900',
        paddingBlock: '800',
      }}
    >
      <PropertiesSelect />
      {hasMfhRoles && pagesWithMap.includes(activeTab) && (
        <ButtonGroup
          aria-label="toggle layout"
          conjoined
          orientation="horizontal"
          role="group"
          css={{ marginBlockStart: { base: '600', md: '0' } }}
        >
          <ToggleButton
            value={MainLayoutType.REPORT}
            selected={layout === MainLayoutType.REPORT}
            icon={
              <Icon>
                <IconTrendingFilled />
              </Icon>
            }
            onClick={() => {
              if (user) {
                const reportEmitMap: Partial<Record<TabsEnum, (args: { user: ZrmClickstreamUser }) => void>> = {
                  [TabsEnum.OVERVIEW]: emitOverviewSwitchToReportViewEvent,
                  [TabsEnum.MARKET_DATA]: emitMarketDataSwitchToReportViewEvent,
                  [TabsEnum.AD_SPEND]: emitAdSpendSwitchToReportViewEvent,
                };
                reportEmitMap[activeTab]?.({ user });
              }
              setLayout(MainLayoutType.REPORT);
            }}
            disabled={isSingleFamilyPropertySelected}
          >
            Report
          </ToggleButton>
          <ToggleButton
            value={MainLayoutType.MAP}
            selected={layout === MainLayoutType.MAP}
            icon={
              <Icon>
                <IconLocationFilled />
              </Icon>
            }
            onClick={() => {
              if (user) {
                const mapEmitMap: Partial<Record<TabsEnum, (args: { user: ZrmClickstreamUser }) => void>> = {
                  [TabsEnum.OVERVIEW]: emitOverviewSwitchToMapViewEvent,
                  [TabsEnum.MARKET_DATA]: emitMarketDataSwitchToMapViewEvent,
                  [TabsEnum.AD_SPEND]: emitAdSpendSwitchToMapViewEvent,
                };
                mapEmitMap[activeTab]?.({ user });
              }
              setLayout(MainLayoutType.MAP);
            }}
            disabled={isSingleFamilyPropertySelected}
          >
            Map
          </ToggleButton>
        </ButtonGroup>
      )}
    </Box>
  );
};
